import { Box, Chip, Stack, Switch, Typography } from "@mui/material";
import { WebinarPromotionTeamMember } from "src/queries/useWebinarPromotionRoutingLogic";
import { useUpdateWebinarPromotionRoutingLogicUser } from "src/mutations/useUpdateWebinarPromotionRoutingLogicUser";

interface WebinarPromotionTeamListProps {
  teamMembers: WebinarPromotionTeamMember[];
  teamName?: string;
  loading?: boolean;
  templateId?: string | number;
}

export function WebinarPromotionTeamList({
  teamMembers,
  loading = false,
  templateId,
}: WebinarPromotionTeamListProps) {
  const updateRoutingLogicUser = useUpdateWebinarPromotionRoutingLogicUser();
  if (loading) {
    return (
      <Stack
        sx={{
          flexShrink: 0,
          backgroundColor: "#F8FBFF",
          padding: 2.5,
          maxHeight: "400px",
          overflow: "auto",
          borderRadius: 1,
          border: "1px solid #E0E7FF",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading team members...
        </Typography>
      </Stack>
    );
  }

  if (!teamMembers || teamMembers.length === 0) {
    return (
      <Stack
        sx={{
          flexShrink: 0,
          backgroundColor: "#F8FBFF",
          padding: 2.5,
          maxHeight: "400px",
          overflow: "auto",
          borderRadius: 1,
          border: "1px solid #E0E7FF",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No active members found for this team.
        </Typography>
      </Stack>
    );
  }

  const tokenStatus = (member: WebinarPromotionTeamMember) => {
    switch (member.token_status) {
      case "current":
        return (
          <Chip
            size="small"
            label="Connected"
            color="success"
            variant="outlined"
          />
        );

      case "disabled":
        return (
          <Chip
            size="small"
            label="Not Connected"
            color="error"
            variant="filled"
          />
        );
      case "expired":
        return (
          <Chip size="small" label="Expired" color="error" variant="filled" />
        );
      default:
        return (
          <Chip size="small" label="Unknown" color="error" variant="filled" />
        );
    }
  };

  return (
    <Stack
      sx={{
        flexShrink: 0,
        backgroundColor: "#F8FBFF",
        padding: 2.5,
        maxHeight: "400px",
        overflow: "auto",
        borderRadius: 1,
        border: "1px solid #E0E7FF",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: 18, marginBottom: 2 }}>
          Promotion Team Members
        </Typography>
      </Box>
      <Stack
        sx={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          pb: 1,
          borderBottom: "1px solid #E6E8F0",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 12,
            color: "text.secondary",
            textTransform: "uppercase",
            minWidth: "80px",
          }}
        >
          Enabled
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 12,
            color: "text.secondary",
            textTransform: "uppercase",
            minWidth: "120px",
          }}
        >
          Name
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 12,
            color: "text.secondary",
            textTransform: "uppercase",
            minWidth: "200px",
          }}
        >
          Email
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 12,
            color: "text.secondary",
            textTransform: "uppercase",
            minWidth: "160px",
          }}
        >
          Calendar Connection
        </Typography>
      </Stack>

      <Stack sx={{ gap: 1 }}>
        {teamMembers
          .sort((a, b) => a.order - b.order)
          .map((member) => {
            const first = (member.first_name || "").trim();
            const last = (member.last_name || "").trim();
            const fullName = `${first} ${last}`.trim();
            return (
              <Stack
                key={member.user_id}
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1,
                  py: 0.5,
                }}
              >
                <Box sx={{ minWidth: "80px" }}>
                  <Switch
                    checked={member.enabled}
                    onChange={async (event) => {
                      if (templateId) {
                        try {
                          await updateRoutingLogicUser(
                            templateId,
                            member.user_id,
                            { enabled: event.target.checked },
                          );
                        } catch (error) {
                          console.error(
                            "Failed to update routing logic:",
                            error,
                          );
                        }
                      }
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: "medium",
                    minWidth: "120px",
                    fontSize: "13px",
                    lineHeight: 1.2,
                    fontStyle: "normal",
                  }}
                >
                  {fullName || "---"}
                </Typography>
                <Typography
                  title={member.email}
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                    minWidth: "200px",
                    fontSize: "13px",
                    lineHeight: 1.2,
                  }}
                >
                  {member.email}
                </Typography>
                <Typography
                  title={member.token_status}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                    fontSize: "13px",
                    lineHeight: 1.2,
                    minWidth: "160px",
                  }}
                >
                  {tokenStatus(member)}
                </Typography>
              </Stack>
            );
          })}
      </Stack>
    </Stack>
  );
}

export default WebinarPromotionTeamList;
